import { HAIRSP } from '../consts/characters';
import {
  FATHOM_TO_NAUTICAL_MILE,
  FOOT_TO_MILE,
  METER_TO_FATHOM,
  METER_TO_FOOT,
  METER_TO_KM,
  METER_TO_MILE,
  METER_TO_NAUTICAL_MILE,
} from '../consts/units';
import {
  elevationUnitByUnitType,
  intoMeters,
  paceUnitByUnitType,
  velocityUnitByUnitType,
} from '../functions/units';
import { floorTo } from '../roundTo';
import { type DistanceUnit, Unit, UnitType } from '../types/units';

/**
 * Returns the display label for the distance unit of a given UnitType.
 *
 * @param unit - The unit system.
 * @returns The distance unit label string.
 *
 * @example
 * getDistanceUnitLabel(UnitType.METRIC)   // → 'km'
 * getDistanceUnitLabel(UnitType.IMPERIAL) // → 'mi'
 * getDistanceUnitLabel(UnitType.NAUTICAL) // → 'nmi'
 */
export function getDistanceUnitLabel(unit: UnitType): string {
  switch (unit) {
    case UnitType.METRIC:
      return Unit.Distance.km;
    case UnitType.IMPERIAL:
      return Unit.Distance.mi;
    case UnitType.NAUTICAL:
      return Unit.Distance.nmi;
    default:
      return '';
  }
}

/**
 * Builds `Intl.NumberFormatOptions` for formatting a distance value.
 * Note: `'nautical-mile'` is not a valid Intl unit — the caller must append the unit string manually.
 *
 * @param unitType - The unit system (metric, imperial, or nautical).
 * @param digits - Number of fraction digits.
 * @param showUnit - Whether to include the unit in the formatted output.
 * @returns `Intl.NumberFormatOptions` appropriate for the given unit system.
 */
function distanceNumberFormatOptions(
  unitType: UnitType,
  digits: number,
  showUnit: boolean,
): Intl.NumberFormatOptions {
  if (unitType === UnitType.NAUTICAL) {
    // 'nautical-mile' is not a valid Intl unit — caller must append the unit string manually
    return { minimumFractionDigits: digits, maximumFractionDigits: digits, style: 'decimal' };
  }
  return {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    unit: unitType === UnitType.IMPERIAL ? 'mile' : 'kilometer',
    style: showUnit ? 'unit' : 'decimal',
  };
}

/**
 * Formats a distance value as a localised string with an optional unit label.
 *
 * The expected input unit depends on the chosen system:
 * - Metric: meters
 * - Imperial: feet
 * - Nautical: fathoms
 *
 * Pass `null` or `undefined` to render a placeholder dash.
 *
 * @param valueInMeterOrFootOrFathom - Distance in the native unit of the chosen system.
 * @param unitType - The desired output unit system.
 * @param digits - Decimal places (default: 2).
 * @param showUnit - Whether to append the unit label (default: `true`).
 * @returns A formatted distance string.
 *
 * @example
 * labelFormatDistance(5000, UnitType.METRIC)            // → '5.00 km'
 * labelFormatDistance(null, UnitType.IMPERIAL)          // → '- mi'
 * labelFormatDistance(10, UnitType.NAUTICAL, 1, false)  // → '0.1'
 */
export const labelFormatDistance = (
  valueInMeterOrFootOrFathom: number | null | undefined,
  unitType: UnitType,
  digits = 2,
  showUnit = true,
) => {
  const options = distanceNumberFormatOptions(unitType, digits, showUnit);

  if (valueInMeterOrFootOrFathom == null) {
    const unitString = !showUnit ? '' : getDistanceUnitLabel(unitType);
    return `-${HAIRSP}${unitString}`;
  }
  if (unitType === UnitType.IMPERIAL) {
    return `${(valueInMeterOrFootOrFathom * FOOT_TO_MILE).toLocaleString(undefined, options)}`;
  }
  if (unitType === UnitType.NAUTICAL) {
    // 'nautical-mile' is not a valid Intl unit, so we append the unit string manually
    const formatted = (valueInMeterOrFootOrFathom * FATHOM_TO_NAUTICAL_MILE).toLocaleString(
      undefined,
      options,
    );
    return showUnit ? `${formatted}${HAIRSP}${Unit.Distance.nmi}` : formatted;
  }
  return `${(valueInMeterOrFootOrFathom * METER_TO_KM).toLocaleString(undefined, options)}`;
};

/**
 * Builds `Intl.NumberFormatOptions` for formatting an elevation value.
 * Note: `'fathom'` is not a valid Intl unit — the caller must append the unit string manually.
 *
 * @param unitType - The unit system (metric, imperial, or nautical).
 * @param digits - Number of fraction digits.
 * @param showUnit - Whether to include the unit in the formatted output.
 * @returns `Intl.NumberFormatOptions` appropriate for the given unit system.
 */
function elevationNumberFormatOptions(
  unitType: UnitType,
  digits: number,
  showUnit: boolean,
): Intl.NumberFormatOptions {
  if (unitType === UnitType.NAUTICAL) {
    // 'fathom' is not a valid Intl unit — caller must append the unit string manually
    return { minimumFractionDigits: digits, maximumFractionDigits: digits, style: 'decimal' };
  }
  return {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    unit: unitType === UnitType.IMPERIAL ? 'foot' : 'meter',
    style: showUnit ? 'unit' : 'decimal',
  };
}

/**
 * Formats an elevation value as a localised string with an optional unit label.
 *
 * The expected input unit depends on the chosen system:
 * - Metric: meters
 * - Imperial: feet
 * - Nautical: fathoms
 *
 * @param valueInMeterOrFootOrFathom - Elevation in the native unit of the chosen system.
 * @param unitType - The desired output unit system.
 * @param digits - Decimal places (default: 2).
 * @param showUnit - Whether to append the unit label (default: `true`).
 * @returns A formatted elevation string.
 *
 * @example
 * labelFormatElevation(1500, UnitType.METRIC)       // → '1,500.00 m'
 * labelFormatElevation(500, UnitType.IMPERIAL, 0)   // → '500 ft'
 */
export const labelFormatElevation = (
  valueInMeterOrFootOrFathom: number,
  unitType: UnitType,
  digits = 2,
  showUnit = true,
) => {
  const options = elevationNumberFormatOptions(unitType, digits, showUnit);

  if (valueInMeterOrFootOrFathom == null)
    return `-${HAIRSP}${!showUnit ? '' : elevationUnitByUnitType(unitType)}`;

  if (unitType === UnitType.NAUTICAL) {
    // 'fathom' is not a valid Intl unit, so we append the unit string manually
    const formatted = valueInMeterOrFootOrFathom.toLocaleString(undefined, options);
    return showUnit ? `${formatted}${HAIRSP}${Unit.Elevation.ftm}` : formatted;
  }

  return valueInMeterOrFootOrFathom.toLocaleString(undefined, options);
};

/**
 * Builds `Intl.NumberFormatOptions` for formatting a velocity value.
 * Note: `'nautical-mile-per-hour'` is not universally supported as an Intl unit —
 * the caller must append the unit string manually for nautical.
 *
 * @param unitType - The unit system (metric, imperial, or nautical).
 * @param digits - Number of fraction digits.
 * @param showUnit - Whether to include the unit in the formatted output.
 * @returns `Intl.NumberFormatOptions` appropriate for the given unit system.
 */
function velocityNumberFormatOptions(
  unitType: UnitType,
  digits: number,
  showUnit: boolean,
): Intl.NumberFormatOptions {
  if (unitType === UnitType.NAUTICAL) {
    // 'nautical-mile-per-hour' is not universally supported as an Intl unit — caller must append manually
    return { minimumFractionDigits: digits, maximumFractionDigits: digits, style: 'decimal' };
  }
  return {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    unit: unitType === UnitType.IMPERIAL ? 'mile-per-hour' : 'kilometer-per-hour',
    style: showUnit ? 'unit' : 'decimal',
  };
}

/**
 * Formats a velocity value as a localised string with an optional unit label.
 *
 * The value must already be in the target unit:
 * - Metric: km/h
 * - Imperial: mph
 * - Nautical: knots
 *
 * Pass `null` or `undefined` to render a placeholder dash.
 *
 * @param valueInKMHorMIHorKnot - Speed in the target unit.
 * @param unitType - The desired output unit system.
 * @param digits - Decimal places (default: 2).
 * @param showUnit - Whether to append the unit label (default: `true`).
 * @returns A formatted velocity string.
 *
 * @example
 * labelFormatVelocity(42.5, UnitType.METRIC)          // → '42.50 km/h'
 * labelFormatVelocity(null, UnitType.NAUTICAL)        // → '- kn'
 * labelFormatVelocity(30, UnitType.IMPERIAL, 1, false) // → '30.0'
 */
export const labelFormatVelocity = (
  valueInKMHorMIHorKnot: number,
  unitType: UnitType,
  digits = 2,
  showUnit = true,
) => {
  const options = velocityNumberFormatOptions(unitType, digits, showUnit);

  if (valueInKMHorMIHorKnot == null) {
    const unitString = !showUnit ? '' : velocityUnitByUnitType(unitType);
    return `-${HAIRSP}${unitString}`;
  }

  if (unitType === UnitType.NAUTICAL) {
    // 'nautical-mile-per-hour' is not universally supported as an Intl unit, so we append manually
    const formatted = valueInKMHorMIHorKnot.toLocaleString(undefined, options);
    return showUnit ? `${formatted}${HAIRSP}${Unit.Velocity.kn}` : formatted;
  }

  return valueInKMHorMIHorKnot.toLocaleString(undefined, options);
};

/**
 * Converts a pace expressed in decimal minutes into a `mm:ss[.fraction]` clock string.
 * Handles rounding carry-over (e.g. 4:59.999 rounds to 5:00.000).
 *
 * @param paceInMinutes - Pace as decimal minutes (e.g. `4.5` = 4 min 30 s).
 * @param digits - Sub-second decimal places (0 = whole seconds).
 * @returns A clock-formatted pace string.
 *
 * @example
 * formatPaceAsClock(4.5, 0)    // → '4:30'
 * formatPaceAsClock(4.5, 2)    // → '4:30.00'
 * formatPaceAsClock(4.9999, 2) // → '5:00.00'
 */
function formatPaceAsClock(paceInMinutes: number, digits: number): string {
  const safeDigits = Math.max(0, digits);
  const factor = 10 ** safeDigits;

  let minutes = Math.floor(paceInMinutes);
  const secondsRaw = (paceInMinutes - minutes) * 60;

  // Round seconds first and then normalize e.g. 4:59.999 -> 5:00.000
  let secondsRounded = Math.round(secondsRaw * factor) / factor;
  if (secondsRounded >= 60) {
    minutes += 1;
    secondsRounded = 0;
  }

  const [secondsIntegerPart, secondsFractionPart] = secondsRounded.toFixed(safeDigits).split('.');
  const paddedSecondsIntegerPart = secondsIntegerPart.padStart(2, '0');

  if (secondsFractionPart == null) {
    return `${minutes}:${paddedSecondsIntegerPart}`;
  }

  return `${minutes}:${paddedSecondsIntegerPart}.${secondsFractionPart}`;
}

/**
 * Formats a pace value as a `mm:ss[.fraction]` clock string with an optional unit label.
 *
 * Negative values and `null`/`undefined` render a placeholder dash.
 *
 * @param valueInMinPerKmOrMinPerMileOrMinPerNmi - Pace in the native unit of the chosen system
 *   (min/km, min/mi, or min/nmi).
 * @param unitType - The desired output unit system.
 * @param digits - Sub-second decimal places (default: 2).
 * @param showUnit - Whether to append the pace unit label (default: `true`).
 * @returns A formatted pace string.
 *
 * @example
 * labelFormatPace(4.5, UnitType.METRIC)            // → '4:30.00 min/km'
 * labelFormatPace(null, UnitType.IMPERIAL)         // → '- min/mi'
 * labelFormatPace(4.5, UnitType.METRIC, 0, false)  // → '4:30'
 */
export const labelFormatPace = (
  valueInMinPerKmOrMinPerMileOrMinPerNmi: number,
  unitType: UnitType,
  digits = 2,
  showUnit = true,
) => {
  if (
    valueInMinPerKmOrMinPerMileOrMinPerNmi == null ||
    valueInMinPerKmOrMinPerMileOrMinPerNmi < 0
  ) {
    const unitString = !showUnit ? '' : paceUnitByUnitType(unitType);
    return `-${HAIRSP}${unitString}`;
  }

  const formatted = formatPaceAsClock(valueInMinPerKmOrMinPerMileOrMinPerNmi, digits);
  if (!showUnit) return formatted;
  return `${formatted}${HAIRSP}${paceUnitByUnitType(unitType)}`;
};

interface FormatDistanceOptions {
  value: number | null | undefined;
  inputUnit: DistanceUnit;
  unitType: UnitType;
  digits?: number;
  maxLength?: number;
  padZeros?: boolean;
  useDecimalsBelow?: number;
}
/**
 * Formats a distance that’s given in any supported unit.
 * Internally converts to meters, then outputs either km (metric) or mi / ft (imperial).
 *
 * @param value - Numeric distance in `inputUnit`. Pass `null` or `undefined` for an empty string.
 * @param inputUnit - The unit of the provided distance (e.g. m, km, mi, ft).
 * @param unitType - Desired output system (`UnitType.METRIC`, `UnitType.IMPERIAL`, or `UnitType.NAUTICAL`).
 * @param digits - Decimal places for the formatted output (default: 2).
 * @param maxLength - Max character length of the numeric part before switching to exponential notation (default: 7).
 * @param padZeros - If `true`, always show fixed decimals (e.g. `110.0`); if `false`, no trailing zeros (default: `false`).
 * @param useDecimalsBelow - If provided, decimals are shown only while `|value| < useDecimalsBelow`; otherwise rounded to integer.
 *
 * @example
 * formatDistance({ value: 5000, inputUnit: 'm', unitType: UnitType.METRIC })            // → '5 km'
 * formatDistance({ value: 500, inputUnit: 'm', unitType: UnitType.METRIC })             // → '500 m'
 * formatDistance({ value: 5000, inputUnit: 'm', unitType: UnitType.IMPERIAL })          // → '3.10 mi'
 * formatDistance({ value: 400, inputUnit: 'm', unitType: UnitType.IMPERIAL })           // → '1312.33 ft'
 */
export function formatDistance({
  value,
  inputUnit,
  unitType,
  digits = 2,
  maxLength = 7,
  padZeros = false,
  useDecimalsBelow,
}: FormatDistanceOptions): string {
  if (value == null || !Number.isFinite(value)) return '';
  const meters = intoMeters(value, inputUnit);

  if (unitType === UnitType.IMPERIAL) {
    // If the imperial “distance” is under 1 mile, we might want foot:
    const milesValue = meters * METER_TO_MILE;
    if (milesValue < 1) {
      return formatAsFoots(meters, digits);
    }
    return formatAsMiles({ meters, digits, maxLength, padZeros, useDecimalsBelow });
  }

  if (unitType === UnitType.NAUTICAL) {
    // If the nautical “distance” is under 1 nautical mile, we might want foot:
    const nauticalMilesValue = meters * METER_TO_NAUTICAL_MILE;
    if (nauticalMilesValue < 1) {
      return formatAsFathoms(meters, digits);
    }
    return formatAsNauticalMiles({ meters, digits, maxLength, padZeros, useDecimalsBelow });
  }

  // Metric: under 1 km → show meters; otherwise km
  const kmValue = meters * METER_TO_KM;
  if (kmValue < 1) {
    return formatAsMeters({ meters, digits, maxLength, padZeros, useDecimalsBelow });
  }
  return formatAsKM({ meters, digits, maxLength, padZeros, useDecimalsBelow });
}

/**
 * Helper function to format a numeric distance value already in “display units”.
 *
 * @param value - The distance value already in the target display unit (km, mi, nmi, m, ft, etc.).
 * @param digits - The number of decimal places for floor rounding.
 * @param maxLength - The maximum character length of the numeric part before switching to exponential notation.
 * @param suffix - Unit suffix to append (e.g. `'km'`, `'mi'`, `'m'`, `'ft'`).
 * @param padZeros - If `true`, always show fixed decimals (e.g. `110.0`); if `false`, no trailing zeros.
 * @param useDecimalsBelow - If provided, decimals are shown only while `|value| < useDecimalsBelow`;
 *   otherwise the value is rounded to an integer.
 * @returns A formatted string with the numeric value and unit suffix.
 *
 * @example
 * formatDistanceValueWithUnit(5.0, 2, 7, 'km', false)           // → '5 km'
 * formatDistanceValueWithUnit(5.0, 2, 7, 'km', true)            // → '5.00 km'
 * formatDistanceValueWithUnit(3.10686, 2, 7, 'mi', false)       // → '3.10 mi'
 */
export function formatDistanceValueWithUnit(
  value: number,
  digits: number,
  maxLength: number,
  suffix: string,
  padZeros: boolean,
  useDecimalsBelow?: number,
): string {
  // Show decimals only when value is below a threshold
  let useFractional = true;
  if (useDecimalsBelow) {
    useFractional = Math.abs(value) < useDecimalsBelow;
  }

  const roundingDigits = useFractional ? digits : 0;
  const rounded = floorTo(value, roundingDigits);

  let valueStr: string;
  if (useFractional && roundingDigits > 0) {
    valueStr = padZeros ? rounded.toFixed(roundingDigits) : rounded.toString();
  } else {
    valueStr = rounded.toString();
  }

  if (valueStr.includes('.')) {
    if (valueStr.length > maxLength) {
      const integerPart = valueStr.split('.')[0];
      valueStr = integerPart.length <= maxLength ? integerPart : value.toExponential(2);
    }
  } else if (valueStr.length > maxLength) {
    valueStr = value.toExponential(2);
  }

  return `${valueStr}${HAIRSP}${suffix}`;
}

interface FormatUnitDistanceOptions {
  meters: number | null | undefined;
  digits?: number;
  maxLength?: number;
  padZeros?: boolean;
  useDecimalsBelow?: number;
}

/**
 * Formats a distance in meters as kilometres.
 *
 * @param meters - Distance in meters.
 * @param digits - Decimal places (default: 2).
 * @param maxLength - Max character length of the numeric part (default: 7).
 * @param padZeros - Preserve trailing zeros (default: `false`).
 * @param useDecimalsBelow - Show decimals only while `|value| < useDecimalsBelow`.
 * @returns Formatted distance string, or `''` for non-finite / null input.
 *
 * @example
 * formatAsKM({ meters: 5000 })                       // → '5 km'
 * formatAsKM({ meters: 5000, padZeros: true })       // → '5.00 km'
 * formatAsKM({ meters: 12345.6, digits: 1 })         // → '12.3 km'
 */
export function formatAsKM({
  meters,
  digits = 2,
  maxLength = 7,
  padZeros = false,
  useDecimalsBelow,
}: FormatUnitDistanceOptions): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  const kmValue = meters / 1000;
  return formatDistanceValueWithUnit(
    kmValue,
    digits,
    maxLength,
    Unit.Distance.km,
    padZeros,
    useDecimalsBelow,
  );
}

/**
 * Formats a distance in meters as a meters string.
 *
 * @param meters - Distance in meters.
 * @param digits - Decimal places (default: 2).
 * @param maxLength - Max character length of the numeric part (default: 7).
 * @param padZeros - Preserve trailing zeros (default: `false`).
 * @param useDecimalsBelow - Show decimals only while `|value| < useDecimalsBelow`.
 * @returns Formatted distance string, or `''` for non-finite / null input.
 *
 * @example
 * formatAsMeters({ meters: 750 })             // → '750 m'
 * formatAsMeters({ meters: 0.5, digits: 1 })  // → '0.5 m'
 */
export function formatAsMeters({
  meters,
  digits = 2,
  maxLength = 7,
  padZeros = false,
  useDecimalsBelow,
}: FormatUnitDistanceOptions): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  return formatDistanceValueWithUnit(
    meters,
    digits,
    maxLength,
    Unit.Distance.m,
    padZeros,
    useDecimalsBelow,
  );
}

/**
 * Formats a distance in meters as miles.
 *
 * @param meters - Distance in meters.
 * @param digits - Decimal places (default: 2).
 * @param maxLength - Max character length of the numeric part (default: 7).
 * @param padZeros - Preserve trailing zeros (default: `false`).
 * @param useDecimalsBelow - Show decimals only while `|value| < useDecimalsBelow`.
 * @returns Formatted distance string, or `''` for non-finite / null input.
 *
 * @example
 * formatAsMiles({ meters: 5000 })  // → '3.10 mi'
 */
export function formatAsMiles({
  meters,
  digits = 2,
  maxLength = 7,
  padZeros = false,
  useDecimalsBelow,
}: FormatUnitDistanceOptions): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  const milesValue = meters * METER_TO_MILE;
  return formatDistanceValueWithUnit(
    milesValue,
    digits,
    maxLength,
    Unit.Distance.mi,
    padZeros,
    useDecimalsBelow,
  );
}

/**
 * Formats a distance in meters as feet.
 *
 * @param meters - Distance in meters. Pass `null` or `undefined` for an empty string.
 * @param digits - Decimal places (default: 2).
 * @returns Formatted distance string, or `''` for null / non-finite input.
 *
 * @example
 * formatAsFoots(50)    // → '164.04 ft'
 * formatAsFoots(null)  // → ''
 */
export function formatAsFoots(meters: number | null | undefined, digits = 2): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  const footValue = meters * METER_TO_FOOT; //  3.2808;
  return `${floorTo(footValue, digits)}${HAIRSP}${Unit.Distance.ft}`;
}

/**
 * Formats a distance in meters as nautical miles.
 *
 * @param meters - Distance in meters.
 * @param digits - Decimal places (default: 2).
 * @param maxLength - Max character length of the numeric part (default: 7).
 * @param padZeros - Preserve trailing zeros (default: `false`).
 * @param useDecimalsBelow - Show decimals only while `|value| < useDecimalsBelow`.
 * @returns Formatted distance string, or `''` for non-finite / null input.
 *
 * @example
 * formatAsNauticalMiles({ meters: 5000 })  // → '2.70 nmi'
 */
export function formatAsNauticalMiles({
  meters,
  digits = 2,
  maxLength = 7,
  padZeros = false,
  useDecimalsBelow,
}: FormatUnitDistanceOptions): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  const nauticalMilesValue = meters * METER_TO_NAUTICAL_MILE;
  return formatDistanceValueWithUnit(
    nauticalMilesValue,
    digits,
    maxLength,
    Unit.Distance.nmi,
    padZeros,
    useDecimalsBelow,
  );
}

/**
 * Formats a distance in meters as fathoms.
 *
 * @param meters - Distance in meters. Pass `null` or `undefined` for an empty string.
 * @param digits - Decimal places (default: 2).
 * @returns Formatted distance string, or `''` for null / non-finite input.
 *
 * @example
 * formatAsFathoms(50)    // → '27.34 ftm'
 * formatAsFathoms(null)  // → ''
 */
export function formatAsFathoms(meters: number | null | undefined, digits = 2): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  const fathomValue = meters * METER_TO_FATHOM;
  return `${floorTo(fathomValue, digits)}${HAIRSP}${Unit.Distance.ftm}`;
}

/**
 * Formats a speed expressed as meters-per-second into the target unit system.
 *
 * @param offset - Speed in meters per second. Pass `null` or `undefined` for an empty string.
 * @param unitType - Target unit system (`'metric'`, `'imperial'`, or `'nautical'`).
 * @param digits - Decimal places (default: 1).
 * @returns Formatted speed string, or `''` for null / non-finite input.
 *
 * @example
 * formatSpeed(10, UnitType.METRIC)    // → '36.0 km/h'
 * formatSpeed(10, UnitType.IMPERIAL)  // → '22.3 mph'
 * formatSpeed(10, UnitType.NAUTICAL)  // → '19.3 kn'
 */
export function formatSpeed(
  offset: number | null | undefined,
  unitType: string,
  digits = 1,
): string {
  if (offset == null || !Number.isFinite(offset)) return '';
  const speed = offset * 3600;
  if (unitType === UnitType.IMPERIAL) {
    return `${floorTo(speed * 0.62137, digits)}${HAIRSP}${Unit.Velocity.mph}`;
  }
  if (unitType === UnitType.NAUTICAL) {
    return `${floorTo(speed * 0.539957, digits)}${HAIRSP}${Unit.Velocity.kn}`;
  }
  return `${floorTo(speed, digits)}${HAIRSP}${Unit.Velocity.kmh}`;
}

// Prefix table adapted from https://github.com/ThomWright/format-si-prefix
const NoValue = ' - ';
const PREFIXES: { [key: string]: string } = {
  '24': 'Y',
  '21': 'Z',
  '18': 'E',
  '15': 'P',
  '12': 'T',
  '9': 'G',
  '6': 'M',
  '3': 'k',
  '0': '',
  '-3': 'm',
  '-6': 'µ',
  '-9': 'n',
  '-12': 'p',
  '-15': 'f',
  '-18': 'a',
  '-21': 'z',
  '-24': 'y',
};

/**
 * Formats a number using SI (metric) prefixes, rounded to 3 significant figures.
 * Supports prefixes from yocto (y, 10⁻²⁴) to yotta (Y, 10²⁴).
 *
 * @param num - The number to format.
 * @returns A string with the scaled number and its SI prefix, separated by a space.
 *
 * @example
 * formatSI(0)           // → '0'
 * formatSI(1000)        // → '1 k'
 * formatSI(1500000)     // → '1.5 M'
 * formatSI(0.001)       // → '1 m'
 * formatSI(-2500)       // → '-2.5 k'
 * formatSI(0.000001)    // → '1 µ'
 */
export const formatSI = (num: number): string => {
  if (num === 0) {
    return '0';
  }
  let sig = Math.abs(num);
  let exponent = 0;
  while (sig >= 1000 && exponent < 24) {
    sig /= 1000;
    exponent += 3;
  }
  while (sig < 1 && exponent > -24) {
    sig *= 1000;
    exponent -= 3;
  }

  const signPrefix = num < 0 ? '-' : '';
  if (sig > 1000) {
    // exponent == 24
    // significand can be arbitrarily long
    return `${signPrefix + sig.toFixed(0)} ${PREFIXES[exponent]}`;
  }
  return `${signPrefix + Number.parseFloat(sig.toPrecision(3))} ${PREFIXES[exponent]}`;
};

/**
 * Formats a number using SI prefixes and appends a physical unit string.
 * Returns a fallback placeholder for non-finite or null/undefined values.
 *
 * @param aValue - The number to format. `null`, `undefined`, `±Infinity`, and `NaN` are treated as missing.
 * @param aUnit - Unit string to append after the SI-prefixed number (default: `''`).
 * @param NoValueOerride - Custom placeholder string to use when the value is missing (default: `' - '`).
 * @returns Formatted string like `'1.5 kW'`, or the placeholder on invalid input.
 *
 * @example
 * formatSIWithUnit(1500, 'W')           // → '1.5 kW'
 * formatSIWithUnit(1000000, 'Hz')       // → '1 MHz'
 * formatSIWithUnit(null, 'W')           // → ' - '
 * formatSIWithUnit(null, 'W', 'N/A')    // → 'N/A'
 */
export const formatSIWithUnit = (
  aValue: number | null | undefined,
  aUnit = '',
  NoValueOverride: string | null = null,
): string => {
  if (
    aValue != null &&
    aValue !== Number.POSITIVE_INFINITY &&
    aValue !== Number.NEGATIVE_INFINITY &&
    !Number.isNaN(aValue)
  ) {
    return `${formatSI(aValue)}${aUnit}`.trimEnd();
  }
  return NoValueOverride || NoValue;
};
